import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  TextField,
} from "@mui/material";
import { CheckCircle, Cancel, Save, Refresh } from "@mui/icons-material";
import axiosInstance from "../utils/axios";
import { toast } from "react-toastify";

export default function AdminCommissionSettlements() {
  const [settlements, setSettlements] = useState([]);
  const [upiSettings, setUpiSettings] = useState({
    adminUpiId: "",
    adminPhonePeNumber: "",
    adminAccountName: "",
  });
  const [savingSettings, setSavingSettings] = useState(false);

  const fetchSettlements = async () => {
    try {
      const res = await axiosInstance.get("/api/admin/commission-settlements");
      setSettlements(res.data.settlements || []);
      if (res.data.adminPaymentInfo) {
        setUpiSettings(res.data.adminPaymentInfo);
      }
    } catch (err) {
      toast.error("Failed to fetch commission settlements");
    }
  };

  useEffect(() => {
    fetchSettlements();
  }, []);

  const handleSavePaymentSettings = async (e) => {
    e.preventDefault();
    setSavingSettings(true);
    try {
      const res = await axiosInstance.put("/api/admin/payment-settings", upiSettings);
      toast.success(res.data.message);
    } catch (err) {
      toast.error("Failed to update UPI settings");
    } finally {
      setSavingSettings(false);
    }
  };

  const handleVerify = async (restaurantId, status) => {
    try {
      const res = await axiosInstance.put(
        `/api/admin/commission-settlements/${restaurantId}/verify`,
        { status }
      );
      toast.success(res.data.message);
      fetchSettlements();
    } catch (err) {
      toast.error(err.response?.data?.message || "Verification failed");
    }
  };

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight="bold">
            Restaurant Commission Settlements & UPI Settings
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage Super Admin UPI/PhonePe details and verify 7-day commission payments from restaurants
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<Refresh />} onClick={fetchSettlements}>
          Refresh
        </Button>
      </Box>

      {/* ── SUPER ADMIN UPI & PHONEPE SETTINGS CARD ── */}
      <Card sx={{ mb: 4, borderRadius: 2, borderLeft: "5px solid #1976d2" }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Super Admin Receiving UPI & PhonePe Settings (Shown on Owner Dashboard)
          </Typography>
          <form onSubmit={handleSavePaymentSettings}>
            <Grid container spacing={2} alignItems="center" mt={0.5}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Receiver Account Name"
                  value={upiSettings.adminAccountName}
                  onChange={(e) =>
                    setUpiSettings({ ...upiSettings, adminAccountName: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Super Admin UPI ID"
                  value={upiSettings.adminUpiId}
                  onChange={(e) =>
                    setUpiSettings({ ...upiSettings, adminUpiId: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="PhonePe / Mobile Number"
                  value={upiSettings.adminPhonePeNumber}
                  onChange={(e) =>
                    setUpiSettings({ ...upiSettings, adminPhonePeNumber: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  startIcon={<Save />}
                  disabled={savingSettings}
                >
                  {savingSettings ? "Saving..." : "Save"}
                </Button>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>

      {/* ── RESTAURANTS COMMISSION VERIFICATION TABLE ── */}
      <TableContainer component={Paper} elevation={2}>
        <Table>
          <TableHead sx={{ bgcolor: "grey.100" }}>
            <TableRow>
              <TableCell><strong>Restaurant Details</strong></TableCell>
              <TableCell><strong>Commission %</strong></TableCell>
              <TableCell><strong>Unsettled Due</strong></TableCell>
              <TableCell><strong>Under Review (To Verify)</strong></TableCell>
              <TableCell><strong>Total Paid (Verified)</strong></TableCell>
              <TableCell align="center"><strong>Verify Action (Confirm / Cancel)</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {settlements.map((item) => (
              <TableRow
                key={item.restaurantId}
                hover
                sx={{ bgcolor: item.underReviewAmount > 0 ? "#fffbeb" : "inherit" }}
              >
                <TableCell>
                  <Typography fontWeight="bold">{item.restaurantName}</Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Owner: {item.ownerName} | 📞 {item.mobile}
                  </Typography>
                  {item.utrNumber && (
                    <Chip
                      label={`UTR: ${item.utrNumber}`}
                      size="small"
                      color="info"
                      sx={{ mt: 0.5, fontSize: 11 }}
                    />
                  )}
                </TableCell>

                <TableCell>
                  <Chip label={`${item.commissionRate}%`} color="secondary" size="small" />
                </TableCell>

                <TableCell>
                  <Typography fontWeight="bold" color="error.main">
                    ₹{item.unsettledAmount}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    ({item.unsettledCount} orders)
                  </Typography>
                </TableCell>

                <TableCell>
                  <Typography variant="subtitle1" fontWeight="bold" color="#d97706">
                    ₹{item.underReviewAmount}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    ({item.underReviewCount} orders)
                  </Typography>
                </TableCell>

                <TableCell>
                  <Typography fontWeight="bold" color="success.main">
                    ₹{item.paidAmount}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    ({item.paidCount} orders)
                  </Typography>
                </TableCell>

                <TableCell align="center">
                  {item.underReviewCount > 0 ? (
                    <Box display="flex" justifyContent="center" gap={1}>
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        startIcon={<CheckCircle />}
                        onClick={() => handleVerify(item.restaurantId, "PAID")}
                      >
                        Confirm Paid
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        startIcon={<Cancel />}
                        onClick={() => handleVerify(item.restaurantId, "REJECTED")}
                      >
                        Cancel
                      </Button>
                    </Box>
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      No Pending Request
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}